// frontend/src/pages/asset-admin/AssetAdminDashboard.js
/**
 * Asset Admin Dashboard
 * 
 * Overview page for asset custodians showing:
 * - Key metrics (total assets, assigned, unassigned, pending disposal)
 * - Fleet alerts with specific vehicle numbers
 * - License alerts
 * - Assets by category
 * - Disposal summary
 * - Recent transfers
 * 
 * Theme: Red (Admin)
 * Loading: Skeleton screens (per design system)
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  Package, Users, AlertCircle, AlertTriangle,
  ArrowLeftRight, Laptop, Key, Car, FileKey, CreditCard, Fuel, Smartphone,
  ChevronRight, Calendar, DollarSign, TrendingDown, Truck, RotateCcw
} from 'lucide-react';
import { useAssetDashboard, useRecentTransfers, usePendingReturnsForCustodian } from '../../hooks/useAssetInventoryData';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { CountBadge } from '../../components/UI/CountBadge';
import { formatDate } from '../../utils/dateUtils';
import { formatAssetTagForDisplay } from '../../utils/mobileLineDisplay';

// ============================================
// SKELETON LOADING COMPONENTS
// ============================================

const DashboardSkeleton = () => (
  <div className="space-y-6 bg-gray-50 min-h-screen">
    {/* Header Skeleton */}
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
      <div className="flex items-center justify-between animate-pulse">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
          <div className="space-y-2">
            <div className="h-6 bg-gray-200 rounded w-48"></div>
            <div className="h-4 bg-gray-200 rounded w-32"></div>
          </div>
        </div>
        <div className="h-10 bg-gray-200 rounded-full w-32 hidden sm:block"></div>
      </div>
    </div>

    {/* Stats Grid Skeleton */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
            <div className="ml-2 sm:ml-4 space-y-2">
              <div className="h-8 bg-gray-200 rounded w-16"></div>
              <div className="h-4 bg-gray-200 rounded w-24"></div>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Alert Cards Skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {[1, 2].map(i => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-6 animate-pulse">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            <div className="space-y-2">
              <div className="h-5 bg-gray-200 rounded w-32"></div>
              <div className="h-3 bg-gray-200 rounded w-24"></div>
            </div>
          </div>
          <div className="space-y-2">
            {[1, 2, 3].map(j => (
              <div key={j} className="h-10 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      ))}
    </div>

    {/* Category Cards Skeleton */}
    <div className="bg-white rounded-2xl shadow-sm border p-6 animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-40 mb-4"></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div key={i} className="p-4 bg-gray-100 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-6 bg-gray-200 rounded w-8"></div>
                <div className="h-3 bg-gray-200 rounded w-16"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============================================
// STAT CARD COMPONENT
// ============================================
const StatCard = ({ title, value, subtitle, icon: Icon, color = 'red', href }) => {
  const colorClasses = {
    red: 'bg-red-100 text-red-600',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-100 text-purple-600',
    gray: 'bg-gray-100 text-gray-600',
    orange: 'bg-orange-100 text-orange-600',
  };
  
  const content = (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[color]}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">{title}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
  
  if (href) {
    return <Link to={href}>{content}</Link>;
  }
  return content;
};

// ============================================
// CATEGORY ICON COMPONENT
// ============================================
const CategoryIcon = ({ code, className = "w-5 h-5" }) => {
  const icons = {
    IT: Laptop,
    ACC: Key,
    FLT: Car,
    LIC: FileKey,
    OFF: Package,
    FUEL: CreditCard,
    DSL: Fuel,
    TEL: Smartphone,
  };
  const Icon = icons[code] || Package;
  return <Icon className={className} />;
};

// ============================================
// CATEGORY COLOR MAPPING
// ============================================
const getCategoryColor = (code) => {
  const colors = {
    IT: { bg: 'bg-blue-100', text: 'text-blue-600', hover: 'hover:bg-blue-50' },
    ACC: { bg: 'bg-green-100', text: 'text-green-600', hover: 'hover:bg-green-50' },
    FLT: { bg: 'bg-orange-100', text: 'text-orange-600', hover: 'hover:bg-orange-50' },
    LIC: { bg: 'bg-purple-100', text: 'text-purple-600', hover: 'hover:bg-purple-50' },
    OFF: { bg: 'bg-gray-100', text: 'text-gray-600', hover: 'hover:bg-gray-50' },
    FUEL: { bg: 'bg-orange-100', text: 'text-orange-600', hover: 'hover:bg-orange-50' },
    DSL: { bg: 'bg-amber-100', text: 'text-amber-600', hover: 'hover:bg-amber-50' },
    TEL: { bg: 'bg-sky-100', text: 'text-sky-600', hover: 'hover:bg-sky-50' },
  };
  return colors[code] || colors.OFF;
};

// ============================================
// ALERT ITEM COMPONENT
// ============================================
const AlertItem = ({ icon: Icon, text, subtext, color = 'red' }) => {
  const colorClasses = {
    red: 'bg-red-50 text-red-700 border-red-100',
    orange: 'bg-orange-50 text-orange-700 border-orange-100',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-100',
  };
  
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{text}</span>
      </div>
      {subtext && <span className="text-xs opacity-75">{subtext}</span>}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const AssetAdminDashboard = () => {
  const { data: dashboard, isLoading: loadingDashboard, error: dashboardError } = useAssetDashboard();
  const { data: recentTransfers = [], isLoading: loadingTransfers } = useRecentTransfers(10);
  const { data: pendingReturns = [] } = usePendingReturnsForCustodian();
  const pendingReturnsCount = pendingReturns.length;

  // Show skeleton while loading
  if (loadingDashboard) {
    return <DashboardSkeleton />;
  }

  // Error state
  if (dashboardError) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-700 font-medium">Failed to load dashboard</p>
          <p className="text-red-600 text-sm mt-1">Please refresh the page to try again.</p>
        </div>
      </div>
    );
  }

  const fleetAlerts = dashboard?.fleet_alerts || {};
  const licenseAlerts = dashboard?.license_alerts || {};
  const disposalSummary = dashboard?.disposal_summary || {};
  const planningLeadDays = fleetAlerts.planning_lead_days || 42;
  const planningItems = fleetAlerts.due_planning?.length
    ? fleetAlerts.due_planning
    : (fleetAlerts.due_30_days || []);
  const lookaheadItems = fleetAlerts.due_lookahead || [];
  const dataHealth = fleetAlerts.data_health || {};
  const hasDataHealthIssues =
    (dataHealth.missing_count || 0) + (dataHealth.stale_ops_count || 0) > 0;
  const fleetAlertCount =
    (fleetAlerts.overdue_count || 0) +
    (fleetAlerts.due_planning_count || planningItems.length) +
    (fleetAlerts.due_lookahead_count || lookaheadItems.length);
  const hasFleetAlerts = fleetAlertCount > 0 || hasDataHealthIssues;
  const hasLicenseAlerts = (licenseAlerts.expired_count || 0) + (licenseAlerts.expiring_30_count || 0) > 0;

  // Find category IDs from breakdown for "View All" links
  const fleetCategory = dashboard?.category_breakdown?.find(cat => cat.category_code === 'FLT');
  const licenseCategory = dashboard?.category_breakdown?.find(cat => cat.category_code === 'LIC');

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Package className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">Asset Inventory Dashboard</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">Overview of all company assets</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <StatCard 
          title="Total Assets" 
          value={dashboard?.total_assets || 0}
          subtitle="Across all categories"
          icon={Package} 
          color="red"
          href="/asset-admin/assets"
        />
        <StatCard 
          title="Assigned" 
          value={dashboard?.assigned_count || 0}
          subtitle="In use by employees"
          icon={Users} 
          color="blue"
          href="/asset-admin/assets?tab=assigned"
        />
        <StatCard 
          title="Deployed" 
          value={dashboard?.deployed_count || 0}
          subtitle="At project sites"
          icon={Package} 
          color="purple"
          href="/asset-admin/assets?tab=deployed"
        />
        <StatCard 
          title="Unassigned" 
          value={dashboard?.unassigned_count || 0}
          subtitle="Available for deployment"
          icon={Package} 
          color="green"
          href="/asset-admin/assets?tab=available"
        />
        <StatCard 
          title="Pending Disposal" 
          value={dashboard?.pending_disposal_count || 0}
          subtitle="Awaiting approval"
          icon={TrendingDown} 
          color="yellow"
          href="/asset-admin/disposal"
        />
      </div>

      {/* Pending Returns Alert - Action Required */}
      {pendingReturnsCount > 0 && (
        <Link to="/asset-admin/pending-returns" className="block">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl shadow-lg p-4 sm:p-5 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <RotateCcw className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-white truncate">Pending Returns</h3>
                    <CountBadge count={pendingReturnsCount} theme="orange" active size="md" max={9999} className="flex-shrink-0" />
                  </div>
                  <p className="text-xs sm:text-sm text-orange-100 mt-0.5 truncate">
                    {pendingReturnsCount === 1 
                      ? '1 asset return awaiting your confirmation' 
                      : `${pendingReturnsCount} asset returns awaiting your confirmation`}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-white/70 flex-shrink-0" />
            </div>
          </div>
        </Link>
      )}

      {/* Alert Sections */}
      {(hasFleetAlerts || hasLicenseAlerts) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Fleet Alerts */}
          {hasFleetAlerts && (
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Truck className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">Fleet Alerts</h3>
                    <p className="text-xs text-gray-500 truncate">
                      6-week planning horizon · vehicles needing attention
                    </p>
                  </div>
                </div>
                <CountBadge
                  count={fleetAlertCount}
                  theme="admin"
                  size="md"
                  max={9999}
                  className="flex-shrink-0"
                />
              </div>

              {hasDataHealthIssues && (
                <div
                  className="mb-4 p-3 rounded-xl bg-orange-50 border border-orange-200"
                  role="status"
                >
                  <div className="flex items-start gap-2">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-4 h-4 text-orange-600" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-orange-900">
                        Compliance data needs a check
                      </p>
                      <p className="text-xs text-orange-800 mt-0.5">
                        {dataHealth.missing_count || 0} missing expiry
                        {(dataHealth.missing_count || 0) === 1 ? '' : 's'}
                        {' · '}
                        {dataHealth.stale_ops_count || 0} vehicle
                        {(dataHealth.stale_ops_count || 0) === 1 ? '' : 's'} with Ops sync older than{' '}
                        {dataHealth.stale_hours || 36}h. COE is HR-maintained.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {/* Overdue Items */}
                {fleetAlerts.overdue?.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-red-600 uppercase tracking-wide mt-2">Overdue</p>
                    {fleetAlerts.overdue.map((item, idx) => (
                      <div key={`overdue-${idx}`} className="flex items-start justify-between gap-2 p-3 rounded-lg border bg-red-50 text-red-700 border-red-100">
                        <div className="flex items-start gap-2 min-w-0 flex-1">
                          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <span className="text-sm font-medium">{item.vehicle} - {item.type}</span>
                            {item.assignee_name && (
                              <p className="text-xs opacity-75 flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {item.assignee_name} · {item.assignee_id}
                              </p>
                            )}
                            {!item.assignee_name && (
                              <p className="text-xs opacity-60 italic">Unassigned</p>
                            )}
                          </div>
                        </div>
                        <span className="text-xs opacity-75 whitespace-nowrap flex-shrink-0">{formatDate(item.date)}</span>
                      </div>
                    ))}
                  </>
                )}

                {planningItems.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-orange-600 uppercase tracking-wide mt-3">
                      Due within {planningLeadDays} days
                    </p>
                    {planningItems.map((item, idx) => (
                      <div key={`planning-${idx}`} className="flex items-start justify-between gap-2 p-3 rounded-lg border bg-orange-50 text-orange-700 border-orange-100">
                        <div className="flex items-start gap-2 min-w-0 flex-1">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                          <div className="min-w-0">
                            <span className="text-sm font-medium">{item.vehicle} - {item.type}</span>
                            {item.assignee_name && (
                              <p className="text-xs opacity-75 flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {item.assignee_name} · {item.assignee_id}
                              </p>
                            )}
                            {!item.assignee_name && (
                              <p className="text-xs opacity-60 italic">Unassigned</p>
                            )}
                          </div>
                        </div>
                        <span className="text-xs opacity-75 whitespace-nowrap flex-shrink-0">{formatDate(item.date)}</span>
                      </div>
                    ))}
                  </>
                )}

                {lookaheadItems.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-amber-700 uppercase tracking-wide mt-3">
                      Next weekly digest
                    </p>
                    {lookaheadItems.map((item, idx) => (
                      <div key={`lookahead-${idx}`} className="flex items-start justify-between gap-2 p-3 rounded-lg border bg-amber-50 text-amber-800 border-amber-100">
                        <div className="flex items-start gap-2 min-w-0 flex-1">
                          <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                          <div className="min-w-0">
                            <span className="text-sm font-medium">{item.vehicle} - {item.type}</span>
                            {item.assignee_name && (
                              <p className="text-xs opacity-75 flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {item.assignee_name} · {item.assignee_id}
                              </p>
                            )}
                            {!item.assignee_name && (
                              <p className="text-xs opacity-60 italic">Unassigned</p>
                            )}
                          </div>
                        </div>
                        <span className="text-xs opacity-75 whitespace-nowrap flex-shrink-0">{formatDate(item.date)}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
              
              <div className="flex justify-end mt-4">
                <Link 
                  to={fleetCategory ? `/asset-admin/assets?category=${fleetCategory.category_id}` : '/asset-admin/assets?category__code__in=FLT'} 
                  className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  View All
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}

          {/* License Alerts */}
          {hasLicenseAlerts && (
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileKey className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">License Alerts</h3>
                    <p className="text-xs text-gray-500 truncate">Subscriptions needing renewal</p>
                  </div>
                </div>
                <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full text-sm font-bold bg-purple-100 text-purple-700 flex-shrink-0">
                  {(licenseAlerts.expired_count || 0) + (licenseAlerts.expiring_30_count || 0)}
                </span>
              </div>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {/* Expired */}
                {licenseAlerts.expired?.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-red-600 uppercase tracking-wide mt-2">Expired</p>
                    {licenseAlerts.expired.map((lic, idx) => (
                      <div key={`exp-${idx}`} className="flex items-center justify-between p-3 rounded-lg border bg-red-50 text-red-700 border-red-100">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          <div>
                            <span className="text-sm font-medium">{lic.product_name}</span>
                            {lic.assignee && (
                              <p className="text-xs opacity-75 flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {lic.assignee} · {lic.assignee_id}
                              </p>
                            )}
                            {!lic.assignee && lic.total_seats && (
                              <p className="text-xs opacity-75">{lic.total_seats} seats</p>
                            )}
                          </div>
                        </div>
                        <span className="text-xs opacity-75">{formatDate(lic.expiry_date)}</span>
                      </div>
                    ))}
                  </>
                )}
                
                {/* Expiring Soon */}
                {licenseAlerts.expiring_30?.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-orange-600 uppercase tracking-wide mt-3">Expiring in 30 Days</p>
                    {licenseAlerts.expiring_30.map((lic, idx) => (
                      <div key={`exp30-${idx}`} className="flex items-center justify-between p-3 rounded-lg border bg-orange-50 text-orange-700 border-orange-100">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                          <div>
                            <span className="text-sm font-medium">{lic.product_name}</span>
                            {lic.assignee && (
                              <p className="text-xs opacity-75 flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {lic.assignee} · {lic.assignee_id}
                              </p>
                            )}
                            {!lic.assignee && lic.total_seats && (
                              <p className="text-xs opacity-75">{lic.total_seats} seats</p>
                            )}
                          </div>
                        </div>
                        <span className="text-xs opacity-75">{formatDate(lic.expiry_date)}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
              
              <div className="flex justify-end mt-4">
                <Link 
                  to={licenseCategory ? `/asset-admin/assets?category=${licenseCategory.category_id}` : '/asset-admin/assets?category__code__in=LIC'} 
                  className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  View All
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}

          {/* If only one alert type, show placeholder or disposal summary inline */}
          {hasFleetAlerts && !hasLicenseAlerts && disposalSummary.total_count > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Disposal Recovery</h3>
                    <p className="text-xs text-gray-500">Assets disposed this period</p>
                  </div>
                </div>
              </div>
              
              <div className="text-center py-4">
                <p className="text-3xl font-bold text-green-600">
                  ${disposalSummary.total_value?.toLocaleString() || '0'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  from {disposalSummary.total_count || 0} disposed assets
                </p>
              </div>
              
              {disposalSummary.recent?.length > 0 && (
                <div className="space-y-2 mt-4">
                  {disposalSummary.recent.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{formatAssetTagForDisplay(item.asset_tag)}</p>
                        <p className="text-xs text-gray-500">{item.disposal_method}</p>
                      </div>
                      <span className="text-sm font-medium text-green-600">
                        ${item.disposal_amount?.toLocaleString() || '0'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Assets by Category */}
      {dashboard?.category_breakdown && dashboard.category_breakdown.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Assets by Category</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {dashboard.category_breakdown.map(cat => {
              const colors = getCategoryColor(cat.category_code);
              return (
                <Link 
                  key={cat.category_code}
                  to={`/asset-admin/assets?category=${cat.category_id}`}
                  className={`p-4 bg-gray-50 rounded-xl ${colors.hover} transition-colors group`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${colors.bg} rounded-full flex items-center justify-center shadow-sm`}>
                      <CategoryIcon code={cat.category_code} className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900">{cat.count}</p>
                      <p className="text-xs text-gray-500 truncate">{cat.category}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom Row: Disposal Summary & Recent Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Disposal Summary (if not shown above) */}
        {disposalSummary.total_count > 0 && !(hasFleetAlerts && !hasLicenseAlerts) && (
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Disposal Summary</h3>
                  <p className="text-xs text-gray-500">Asset recovery value</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl mb-4">
              <div>
                <p className="text-sm text-green-700">Total Recovery</p>
                <p className="text-2xl font-bold text-green-600">
                  ${disposalSummary.total_value?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-green-700">Assets Disposed</p>
                <p className="text-2xl font-bold text-green-600">{disposalSummary.total_count || 0}</p>
              </div>
            </div>
            
            {disposalSummary.recent?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Recent Disposals</p>
                {disposalSummary.recent.slice(0, 4).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{formatAssetTagForDisplay(item.asset_tag)}</p>
                      <p className="text-xs text-gray-500">
                        {item.brand} {item.model} • {item.disposal_method}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-green-600">
                        ${item.disposal_amount?.toLocaleString() || '0'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatDate(item.disposal_date)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recent Transactions */}
        <div className="bg-white rounded-2xl shadow-sm border">
          <div className="p-4 border-b flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <ArrowLeftRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <h3 className="font-semibold text-gray-900 truncate">Recent Transactions</h3>
            </div>
            <Link to="/asset-admin/transfers" className="text-sm text-red-600 hover:underline flex-shrink-0">
              View all
            </Link>
          </div>
          <div className="p-4">
            {loadingTransfers ? (
              <div className="space-y-3 animate-pulse">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                      <div className="h-3 bg-gray-200 rounded w-32"></div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  </div>
                ))}
              </div>
            ) : recentTransfers.length > 0 ? (
              <div className="space-y-3">
                {recentTransfers.slice(0, 5).map(transfer => (
                  <div key={transfer.id} className="flex items-start justify-between gap-2 p-3 bg-gray-50 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{formatAssetTagForDisplay(transfer.asset_tag)}</p>
                      <p className="text-xs text-gray-500">
                        {transfer.from_employee_name || 'Inventory'} → {transfer.to_employee_name || 'Inventory'}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <StatusBadge status={transfer.status} />
                      <p className="text-xs text-gray-400 mt-1">
                        {formatDate(transfer.effective_date || transfer.transfer_date)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No recent transfers</p>
            )}
          </div>
        </div>

        {/* If no disposal summary, show empty state or fill space */}
        {disposalSummary.total_count === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border p-6 flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <TrendingDown className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="font-medium text-gray-900">No Disposals Yet</h3>
            <p className="text-sm text-gray-500 mt-1">
              Disposed assets and their recovery values will appear here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetAdminDashboard;
